import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  DialogActions,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import PCheckbox from "components/PCheckbox";
import { P } from "theme/typography";
import { useFormik } from "formik";
import { ArrowBack, Delete, Edit, FileUpload, InfoOutlined } from "@mui/icons-material";
import PSelect from "components/PSelect";
import { CLUSTER_TYPES, displayClusterType, displayPageType, isFieldAiConfig, PAGE_TYPES } from "Backend";
import { useAppSelector } from "redux/hooks";
import {
  fetchFieldAiConfigs,
  selectLensEditorAiFieldConfigById,
  selectLensEditorLens,
  selectPageLabels,
} from "redux/slices/lensEditor";
import { runGqlMutation } from "gqlClient";
import {
  FieldAiConfigAttributionMode,
  FieldAiConfigExampleSet,
  FieldAiConfigInputMode,
  FieldAiConfigModelMode,
  FieldAiConfigView,
  UpdateFieldAiConfigDocument,
} from "generated/graphql";
import { useSnackbar } from "notistack";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import StyledAutocomplete from "components/StyledAutocomplete";
import { exists, filterNonEmpty, nonEmpty, stripNullEmpty } from "helpers/coll";
import CustomPropertyQueryForm from "routes/lens-editor/profile-builder/CustomPropertyQueryForm";

interface FieldAIConfigFormProps {
  onCancel: () => void;
  fieldId: number;
}

const ALL_INPUT_MODES: { name: string; value: FieldAiConfigInputMode }[] = Object.entries(FieldAiConfigInputMode).map(
  ([name, value]) => ({
    name: name,
    value: value,
  })
);

const ALL_MODEL_MODES: { name: string; value: FieldAiConfigModelMode }[] = Object.entries(FieldAiConfigModelMode).map(
  ([name, value]) => ({
    name: name,
    value: value,
  })
);

const ALL_ATTRIBUTION_MODES: { name: string; value: FieldAiConfigAttributionMode }[] = Object.entries(
  FieldAiConfigAttributionMode
).map(([name, value]) => ({
  name: name,
  value: value,
}));

const MultiSelectMUI = ({
  options,
  optionsLabelFormatter,
  value,
  onChange,
  label,
}: {
  options: string[];
  optionsLabelFormatter?: (option: any) => string;
  value: string[];
  onChange: (value: string[]) => void;
  label: string;
}) => {
  return (
    <FormControl size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        multiple
        value={value}
        onChange={(e) => {
          onChange(e.target.value as string[]);
        }}>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {optionsLabelFormatter ? optionsLabelFormatter(option) : option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const FieldAiRecordExampleSet = ({
  exampleSet,
  onDelete,
  onEdit,
}: {
  exampleSet: FieldAiConfigExampleSet;
  onDelete: () => void;
  onEdit: () => void;
}) => {
  return (
    <TableRow>
      <TableCell>
        <P>{exampleSet.targets.length > 0 ? exampleSet.targets.join(", ") : "Empty"}</P>
      </TableCell>
      <TableCell>
        <P>
          {exampleSet.example.length > 50
            ? exampleSet.example.substring(0, 50) + "..."
            : exampleSet.example
              ? exampleSet.example
              : "Empty"}
        </P>
      </TableCell>
      {/* Actions */}
      <TableCell align="center">
        <Box
          sx={{
            display: "flex",
          }}>
          <Tooltip title="Delete" arrow>
            <IconButton onClick={onDelete}>
              <Delete />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" arrow>
            <IconButton onClick={onEdit}>
              <Edit />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const FieldAiConfigExampleSetEditorDialog = ({
  selectOptions,
  exampleSet,
  onCancel,
  onSave,
}: {
  selectOptions: string[];
  exampleSet: FieldAiConfigExampleSet;
  onCancel: () => void;
  onSave: (exampleSet: FieldAiConfigExampleSet) => void;
}) => {
  const [formValues, setFormValues] = useState<FieldAiConfigExampleSet>(exampleSet);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    const newFormValues = {
      ...formValues,
      [name]: value,
    };
    setFormValues(newFormValues);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 2,
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}>
        <IconButton onClick={onCancel}>
          <ArrowBack />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: "column",
        }}>
        <StyledAutocomplete
          sx={{ display: "flex", mt: 0.5 }}
          fullWidth
          size="small"
          multiple
          value={formValues.targets as string[]}
          onChange={(_evt, values) => {
            setFormValues({
              ...formValues,
              targets: values,
            });
          }}
          renderInput={(params) => (
            <TextField {...params} color="secondary" id="selectOptions" label="Select Page Types / Labels" />
          )}
          options={selectOptions}
        />
        <TextField
          fullWidth
          label="Example"
          name="example"
          value={formValues.example}
          onChange={handleChange}
          type="text"
          margin="dense"
          multiline
          minRows={6}
        />
      </Box>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <LoadingButton variant="contained" onClick={() => onSave(formValues)}>
          Update
        </LoadingButton>
      </DialogActions>
    </Box>
  );
};

const FieldAIConfigForm: FC<FieldAIConfigFormProps> = ({ onCancel, fieldId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [isPending, setIsPending] = useState(false);
  const [examplesEditOpen, setExamplesEditOpen] = useState(false);
  const [selectedExampleIndex, setSelectedExampleIndex] = useState<number | undefined>(undefined);
  const [selectedExampleSet, setSelectedExampleSet] = useState<FieldAiConfigExampleSet | undefined>(undefined);
  const lensId = useAppSelector(selectLensEditorLens).id;
  const fieldAiConfig = useAppSelector((state) => selectLensEditorAiFieldConfigById(state, fieldId));
  const pageLabels = useAppSelector(selectPageLabels);
  const snackpatch = useAppDispatchWithSnackbar();
  const [importAIConfigOpen, setImportAIConfigOpen] = useState(false);
  const [json, setJson] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);

  const jsonField = useRef<FieldAiConfigView | null>(null);

  const initialValues: FieldAiConfigView = fieldAiConfig || {
    fieldId,
    useReviewConfig: true,
    lensHits: true,
    maxContiguousPages: 1,
    fillNotes: false,
    multiResponse: false,
    alternateName: "",
    alternatePrompt: "",
    inlineFilters: {
      pageTypes: [],
      notPageTypes: [],
      clusterTypes: [],
      notClusterTypes: [],
      pageLabels: [],
      notPageLabels: [],
      includeNegated: false,
      ruleIds: [],
      recommendedOnly: false,
      documentPropertyQueries: [],
    },
    examples: {
      byRecordType: [],
      byLensRuleTypeId: [],
      default: undefined,
    },
    inputMode: FieldAiConfigInputMode.Blocks,
    modelMode: FieldAiConfigModelMode.Default,
    defaultTargetPageSets: undefined,
    chainOfThought: false,
    live: false,
    attributionMode: FieldAiConfigAttributionMode.Raw,
    modelNameOverride: "",
    cellChainOfThought: false,
  };

  const fieldAiConfigForm = useFormik({
    initialValues,
    onSubmit: (values) => {
      setIsPending(true);
      runGqlMutation(UpdateFieldAiConfigDocument, {
        fieldId: values.fieldId,
        useReviewConfig: values.useReviewConfig,
        lensHits: values.lensHits,
        maxContiguousPages: values.maxContiguousPages,
        fillNotes: values.fillNotes,
        multiResponse: values.multiResponse,
        alternateName: stripNullEmpty(values.alternateName),
        alternatePrompt: stripNullEmpty(values.alternatePrompt),
        examples: values.examples,
        inputMode: values.inputMode,
        modelMode: values.modelMode,
        defaultTargetPageSets: values.defaultTargetPageSets,
        chainOfThought: values.chainOfThought,
        live: values.live,
        attributionMode: values.attributionMode,
        modelNameOverride: stripNullEmpty(values.modelNameOverride),
        cellChainOfThought: values.cellChainOfThought,
        inlineFilters: {
          ...values.inlineFilters,
          clusterTypes: filterNonEmpty(values.inlineFilters?.clusterTypes),
          documentPropertyQueries: filterNonEmpty(values.inlineFilters?.documentPropertyQueries),
          notClusterTypes: filterNonEmpty(values.inlineFilters?.notClusterTypes),
          notPageLabels: filterNonEmpty(values.inlineFilters?.notPageLabels),
          notPageTypes: filterNonEmpty(values.inlineFilters?.notPageTypes),
          pageLabels: filterNonEmpty(values.inlineFilters?.pageLabels),
          pageTypes: filterNonEmpty(values.inlineFilters?.pageTypes),
          ruleIds: filterNonEmpty(values.inlineFilters?.ruleIds),
        },
      })
        .then(() => {
          enqueueSnackbar("Field AI Config Saved", { variant: "success" });
          setIsPending(false);
          void snackpatch(fetchFieldAiConfigs({ lensId }), {
            errorMessage: "Failed to fetch field AI configs",
          });
          onCancel();
        })
        .catch((error) => {
          enqueueSnackbar(`Error Saving Field AI Config: ${error.message}`, { variant: "error" });
          setIsPending(false);
        });
    },
    validate: (values) => {
      const errors: Record<string, string> = {};

      if (!values.maxContiguousPages) {
        errors.maxContiguousPages = "Required";
      } else {
        if (isNaN(values.maxContiguousPages)) {
          errors.maxContiguousPages = "Must be a number";
        }
        if (values.maxContiguousPages < 1 || values.maxContiguousPages > 10) {
          errors.maxContiguousPages = "Must be a number between 1 and 10";
        }
      }

      return errors;
    },
  });

  // useEffect the aiFieldConfig and update the values when redux changes
  useEffect(() => {
    if (fieldAiConfig) {
      fieldAiConfigForm.setValues(fieldAiConfig).catch((error) => {
        console.error("Error setting field AI config form values: ", error);
        enqueueSnackbar("Error setting field AI config form values", { variant: "error" });
      });
    }
  }, [fieldAiConfig]);

  const { values, handleChange, touched } = fieldAiConfigForm;
  const anyTouched = exists(Object.entries(fieldAiConfigForm.touched), ([_, v]) => Boolean(v));
  const handleCheckboxChange = (evt: ChangeEvent<any>) => {
    fieldAiConfigForm.handleChange(evt);
    fieldAiConfigForm.setFieldTouched(evt.target["name"], true, true);
  };
  const inlineFilters = values.inlineFilters || {};

  const onDeleteRecordTypeExample = (index: number) => {
    const newExamples = values.examples?.byRecordType?.filter((_fieldAiConfigExamples, i) => i !== index);
    fieldAiConfigForm.setFieldValue("examples.byRecordType", newExamples).catch((error) => {
      console.error("Error deleting record type example: ", error);
      enqueueSnackbar("Error deleting record type example", { variant: "error" });
    });
  };

  const totalExamples = values.examples?.byRecordType?.length || 0;
  const hasDefaultExample = values.examples?.default ? 1 : 0;

  return (
    <Box
      sx={{
        width: "100%",
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}>
        {importAIConfigOpen && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 2,
            }}>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}>
              <IconButton>
                <ArrowBack onClick={() => setImportAIConfigOpen(false)} />
              </IconButton>
              <P variant="xlBold">Import Field AI Config from JSON</P>
            </Box>
            <TextField
              autoFocus
              fullWidth
              multiline
              error={Boolean(jsonError)}
              helperText={jsonError}
              placeholder="Paste JSON here"
              rows={10}
              variant="outlined"
              value={json}
              onChange={(e) => {
                const value = e.target.value;
                setJson(value);
                let parsedValue: any;
                try {
                  parsedValue = JSON.parse(value);
                } catch (error) {
                  setJsonError("Invalid JSON");
                  return;
                }
                if (parsedValue && isFieldAiConfig(parsedValue)) {
                  const formField = parsedValue as unknown as FieldAiConfigView;
                  setJsonError(null);
                  jsonField.current = formField;
                  // add fieldId into the object
                  formField.fieldId = values.fieldId;
                  // update the form values with the parsed JSON
                  fieldAiConfigForm.setValues(formField).catch((error) => {
                    console.error("Error setting field AI config form values: ", error);
                    enqueueSnackbar("Error setting field AI config form values", { variant: "error" });
                  });
                  setImportAIConfigOpen(false);
                } else {
                  // JSON is not a valid field AI config
                  setJsonError(
                    "JSON failed structure validation. Please make sure the JSON is copied directly from the field AI config."
                  );
                }
              }}
            />
          </Box>
        )}
        {selectedExampleSet && !importAIConfigOpen && (
          <FieldAiConfigExampleSetEditorDialog
            selectOptions={[...pageLabels.map((label) => label.name), ...PAGE_TYPES]}
            exampleSet={selectedExampleSet}
            onCancel={() => {
              setSelectedExampleSet(undefined);
              setSelectedExampleIndex(undefined);
            }}
            onSave={(exampleSet) => {
              const newExamples = values.examples?.byRecordType?.map((example, index) =>
                index === selectedExampleIndex ? exampleSet : example
              );
              fieldAiConfigForm.setFieldValue("examples.byRecordType", newExamples).catch((error) => {
                console.error("Error updating examples.byRecordType: ", error);
                enqueueSnackbar("Error updating the field", { variant: "error" });
              });
              setSelectedExampleSet(undefined);
              setSelectedExampleIndex(undefined);
            }}
          />
        )}
        {examplesEditOpen && !selectedExampleSet && !importAIConfigOpen && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 2,
              width: "100%",
            }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}>
              <IconButton>
                <ArrowBack onClick={() => setExamplesEditOpen(false)} />
              </IconButton>
              <P variant="xlBold">Editing AI Config Examples</P>
            </Box>
            <Box>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}>
                  <P variant="smBold">By Record Type </P>
                  <Button
                    size="small"
                    onClick={() => {
                      fieldAiConfigForm.setFieldValue("examples.byRecordType", [
                        ...(values.examples?.byRecordType || []),
                        {
                          targets: [],
                          example: "",
                        },
                      ]);
                    }}>
                    Add By Record Type
                  </Button>
                </Box>
                <Table
                  size="small"
                  sx={{
                    width: "100%",
                    maxHeight: "400px",
                    overflow: "auto",
                  }}
                  stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Targets</TableCell>
                      <TableCell>Example</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {values.examples?.byRecordType?.map((exampleSet, index) => (
                      <FieldAiRecordExampleSet
                        key={index}
                        exampleSet={exampleSet}
                        onDelete={() => onDeleteRecordTypeExample(index)}
                        onEdit={() => {
                          setSelectedExampleSet(exampleSet);
                          setSelectedExampleIndex(index);
                        }}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <TextField
                fullWidth
                label="Default"
                name="examples.default"
                value={values.examples?.default}
                onChange={handleChange}
                onBlur={fieldAiConfigForm.handleBlur}
                type="text"
                margin="dense"
                minRows={3}
                multiline
              />
              <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <LoadingButton variant="contained" onClick={fieldAiConfigForm.submitForm} loading={isPending}>
                  Save
                </LoadingButton>
              </DialogActions>
            </Box>
          </Box>
        )}
        {!examplesEditOpen && !importAIConfigOpen && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 2,
            }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
              <P variant="xlBold">
                Editing {values.live ? "LIVE " : ""} AI Config {anyTouched ? "*" : ""}
              </P>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <P variant="xlBold">Live</P>
                <Switch name="live" checked={values.live} onChange={handleCheckboxChange} />
                <Tooltip title="Import a field AI Config from JSON">
                  <IconButton onClick={() => setImportAIConfigOpen(true)}>
                    <FileUpload />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                <PCheckbox
                  sx={{ mr: 0.25 }}
                  label="Fill Notes"
                  name="fillNotes"
                  checked={values.fillNotes}
                  onChange={handleCheckboxChange}
                />
                <Tooltip title="Includes a 'notes' (prompted as 'summary') at the end of the schema, allowing the model to summarize its output in prose. This is stored in the notes of the profile response.">
                  <InfoOutlined fontSize="small" sx={{ color: "grey.300" }} />
                </Tooltip>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", ml: 2 }}>
                <PCheckbox
                  sx={{ mr: 0.25 }}
                  label="Multi Response"
                  name="multiResponse"
                  checked={values.multiResponse}
                  onChange={handleCheckboxChange}
                />
                <Tooltip title="Prompts for a LIST of responses for EACH PAGESET, rather than a single response for each pageset. Use if the nature of the content repeats on a single page, for instance pharmacy records">
                  <InfoOutlined fontSize="small" sx={{ color: "grey.300" }} />
                </Tooltip>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", ml: 2 }}>
                <PCheckbox
                  sx={{ mr: 0.25 }}
                  label="Field CoT"
                  name="chainOfThought"
                  checked={values.chainOfThought}
                  onChange={handleCheckboxChange}
                />
                <Tooltip title="Adds a chain of thought output field, giving the model space to think out loud and step by step before generating cell values.">
                  <InfoOutlined fontSize="small" sx={{ color: "grey.300" }} />
                </Tooltip>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", ml: 2 }}>
                <PCheckbox
                  sx={{ mr: 0.25 }}
                  label="Cell CoT"
                  name="cellChainOfThought"
                  checked={values.cellChainOfThought}
                  onChange={handleCheckboxChange}
                />
                <Tooltip title="Adds a chain of thought output FOR EACH CELL, giving the model space to think out loud and step by step before generating cell values">
                  <InfoOutlined fontSize="small" sx={{ color: "grey.300" }} />
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel variant="outlined">Input Mode</InputLabel>
                <PSelect autoWidth label="inputMode" name="inputMode" value={values.inputMode} onChange={handleChange}>
                  {ALL_INPUT_MODES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </PSelect>
              </FormControl>
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel variant="outlined">Model Mode</InputLabel>
                <PSelect autoWidth label="modelMode" name="modelMode" value={values.modelMode} onChange={handleChange}>
                  {ALL_MODEL_MODES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </PSelect>
              </FormControl>
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel variant="outlined">Attribution Mode</InputLabel>
                <PSelect
                  autoWidth
                  label="attributionMode"
                  name="attributionMode"
                  value={values.attributionMode}
                  onChange={handleChange}>
                  {ALL_ATTRIBUTION_MODES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </PSelect>
              </FormControl>
              <FormControl size="small" sx={{ width: 200 }}>
                <TextField
                  label="Target Page Sets"
                  type="number"
                  name="defaultTargetPageSets"
                  value={values.defaultTargetPageSets ?? ""}
                  onChange={handleChange}
                />
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <FormControl size="small" sx={{ width: 200 }}>
                <TextField
                  label="Model Override"
                  name="modelNameOverride"
                  value={values.modelNameOverride ?? ""}
                  onChange={handleChange}
                />
              </FormControl>
            </Box>

            <TextField
              label="Alternate Field Name"
              name="alternateName"
              value={values.alternateName ?? ""}
              onChange={handleChange}
              error={touched.alternateName && Boolean(fieldAiConfigForm.errors.alternateName)}
              helperText={touched.alternateName && fieldAiConfigForm.errors.alternateName}
              onBlur={fieldAiConfigForm.handleBlur}
              type="text"
              margin="dense"
            />

            <TextField
              label="Alternate Prompt"
              name="alternatePrompt"
              value={values.alternatePrompt ?? ""}
              onChange={handleChange}
              error={touched.alternatePrompt && Boolean(fieldAiConfigForm.errors.alternatePrompt)}
              helperText={touched.alternatePrompt && fieldAiConfigForm.errors.alternatePrompt}
              onBlur={fieldAiConfigForm.handleBlur}
              type="text"
              margin="dense"
              rows={nonEmpty(values.alternatePrompt) ? 8 : 2}
              multiline
            />
            <TextField
              label="Max Contiguous Pages"
              name="maxContiguousPages"
              value={values.maxContiguousPages}
              onChange={handleChange}
              error={touched.maxContiguousPages && Boolean(fieldAiConfigForm.errors.maxContiguousPages)}
              helperText={touched.maxContiguousPages && fieldAiConfigForm.errors.maxContiguousPages}
              onBlur={fieldAiConfigForm.handleBlur}
              margin="dense"
              type="number"
              inputProps={{ min: 1, max: 10 }}
            />
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", my: 2 }}>
              <P>{totalExamples + hasDefaultExample} Examples</P>
              <Button
                size="small"
                onClick={() => {
                  setExamplesEditOpen(true);
                }}>
                Edit Examples
              </Button>
            </Box>

            <Divider />
            <Box>
              <PCheckbox
                label="Use Review Config"
                name="useReviewConfig"
                checked={values.useReviewConfig}
                onChange={handleCheckboxChange}
              />
              <PCheckbox label="Lens Hits" name="lensHits" checked={values.lensHits} onChange={handleCheckboxChange} />
            </Box>
            <P>Inline Filters</P>
            <Box
              sx={{
                mt: 1,
                ml: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}>
              <MultiSelectMUI
                label="Page Types"
                options={PAGE_TYPES}
                value={(inlineFilters && inlineFilters.pageTypes) || []}
                onChange={(selected) => {
                  fieldAiConfigForm.setFieldValue("inlineFilters.pageTypes", selected);
                }}
                optionsLabelFormatter={displayPageType}
              />
              <MultiSelectMUI
                label="Excluded Page Types"
                options={PAGE_TYPES}
                value={(inlineFilters && inlineFilters.notPageTypes) || []}
                onChange={(selected) => {
                  fieldAiConfigForm.setFieldValue("inlineFilters.notPageTypes", selected);
                }}
                optionsLabelFormatter={displayPageType}
              />
              <MultiSelectMUI
                label="Cluster Types"
                options={CLUSTER_TYPES}
                value={(inlineFilters && inlineFilters.clusterTypes) || []}
                onChange={(selected) => {
                  fieldAiConfigForm.setFieldValue("inlineFilters.clusterTypes", selected);
                }}
                optionsLabelFormatter={displayClusterType}
              />
              <MultiSelectMUI
                label="Not Cluster Types"
                options={CLUSTER_TYPES}
                value={(inlineFilters && inlineFilters.notClusterTypes) || []}
                onChange={(selected) => {
                  fieldAiConfigForm.setFieldValue("inlineFilters.notClusterTypes", selected);
                }}
                optionsLabelFormatter={displayClusterType}
              />
              <MultiSelectMUI
                label="Page Labels"
                options={pageLabels.map((label) => label.name)}
                value={(inlineFilters && inlineFilters.pageLabels) || []}
                onChange={(selected) => {
                  fieldAiConfigForm.setFieldValue("inlineFilters.pageLabels", selected);
                }}
              />
              <MultiSelectMUI
                label="Excluded Page Labels"
                options={pageLabels.map((label) => label.name)}
                value={(inlineFilters && inlineFilters.notPageLabels) || []}
                onChange={(selected) => {
                  fieldAiConfigForm.setFieldValue("inlineFilters.notPageLabels", selected);
                }}
              />
              <PCheckbox
                label="Include Negated"
                name="inlineFilters.includeNegated"
                checked={inlineFilters && inlineFilters.includeNegated ? true : false}
                onChange={handleCheckboxChange}
              />
              <PCheckbox
                label="Recommended Only"
                name="inlineFilters.recommendedOnly"
                checked={inlineFilters.recommendedOnly === true}
                onChange={handleCheckboxChange}
              />
            </Box>
            <P>Document Property Filters</P>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                my: 1,
              }}>
              {inlineFilters.documentPropertyQueries &&
                inlineFilters?.documentPropertyQueries.map((query, index) => (
                  <CustomPropertyQueryForm
                    onChange={(query) => {
                      // update the custom property query in the array based on index
                      const newQueries = (inlineFilters?.documentPropertyQueries || []).map((q, i) =>
                        i === index ? query : q
                      );
                      fieldAiConfigForm.setFieldValue("inlineFilters.documentPropertyQueries", newQueries);
                    }}
                    customPropertyQuery={query}
                    key={index}
                    onDelete={() => {
                      const documentPropertyQueries = inlineFilters.documentPropertyQueries || [];
                      // remove the custom property query from the array based on index
                      const newQueries = documentPropertyQueries.filter((_query, i) => i !== index);
                      fieldAiConfigForm.setFieldValue("inlineFilters.documentPropertyQueries", newQueries);
                    }}
                  />
                ))}
            </Box>
            <Button
              onClick={() => {
                fieldAiConfigForm.setFieldValue("inlineFilters.documentPropertyQueries", [
                  ...(inlineFilters.documentPropertyQueries || []),
                  {
                    key: "",
                    value: null,
                    exact: null,
                    presence: null,
                    excludeValue: null,
                  },
                ]);
              }}>
              Add Document Property Filter
            </Button>
            <DialogActions>
              <Button onClick={onCancel}>Cancel</Button>
              <LoadingButton variant="contained" onClick={fieldAiConfigForm.submitForm} loading={isPending}>
                Save
              </LoadingButton>
            </DialogActions>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FieldAIConfigForm;
